import PageWrapper from '@/components/atoms/PageWrapper';
import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { Button } from '@/components/ui/button';
import { useProjectStore, useSurveyStore } from "@/lib/zustand/useStore";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";


import { Table_NEW } from "@/components/custom/organisms/Table";
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { crmRegistry } from '@/features/crm_system/lib/_core/crmRegistry';
import { useGetExportData } from '@/lib/useQuery/useGetExportData/useGetExportData';
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { Settings } from 'lucide-react';
import BeforeExportDialog from "./components/crm/_shared/BeforeExportDialog";
import { ExportSettings } from './components/ExportSettings/ExportSettings';
import { exportSurveyTableColumns } from "./ExportSurvey.table";
import { countErrorsInRowForFields, fullConfigurationToColumnVisibilityObject } from './utils';
import { DynamicHeightScrollArea } from '@/components/atoms/DynamicHeightScrollArea';



export function ExportSurvey() {
    const { project } = useProjectStore(state => state);
    const crmSystem = project?.crm_system;
    const survey = useSurveyStore(state => state.survey);
    const {
        data: exportData,
        isLoading: isLoadingExportData,
        isError: isErrorExportData
    } = useGetExportData({ projectId: project?.id, surveyId: survey?.id });
    const [hasSetInitialVisibility, setHasSetInitialVisibility] = useState(false);
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    useEffect(() => {
        if (!hasSetInitialVisibility && exportData && exportData?.matched_configuration_with_default) {
            setColumnVisibility(fullConfigurationToColumnVisibilityObject(exportData?.matched_configuration_with_default))
            setHasSetInitialVisibility(true);
        }
    }, [exportData, hasSetInitialVisibility])


    const visibleColumns = useMemo(() =>
        Object.keys(columnVisibility).filter((key) => columnVisibility[key] === true),
        [columnVisibility]
    );
    const invalidRows = exportData?.refinedData?.filter(row => countErrorsInRowForFields(row, visibleColumns) > 0).length;

    const [showExportModal, setShowExportModal] = useState(false);
    const beforeExportSurveyHandler = () => {
        if (invalidRows !== 0) {
            setShowExportModal(true);
            return;
        }

        exportSurveyHandler(false);
    }

    const closeBeforeExportSurveyModal = () => {
        setShowExportModal(false);
    }

    const exportSurveyHandler = async (exportInvalidRows: boolean) => {
        console.log(survey)
        if (!survey) return;
        if(!crmSystem) throw new Error("No CRM system found in export data");
        crmRegistry.getExportHandler(crmSystem)({
            data: exportData,
            selectedColumns: visibleColumns,
            exportInvalidRows
        })
        setShowExportModal(false);
    }

    // Memoize columns to prevent infinite rerenders
    const columns = useMemo(() => {
        return exportSurveyTableColumns({
            columnVisibility,
            data: exportData
        });
    }, [columnVisibility, exportData]);

    const table = useReactTable({
        columns,
        data: exportData?.refinedData ?? [],
        getCoreRowModel: getCoreRowModel(),
        state: {
            columnVisibility
        },
        columnResizeMode: 'onChange',
        onColumnVisibilityChange: setColumnVisibility
    })
    return (
        <>
            <BeforeExportDialog
                invalidRows={invalidRows ?? 0}
                showExportModal={showExportModal}
                closeBeforeExportSurveyModal={closeBeforeExportSurveyModal}
                exportSurveyHandler={exportSurveyHandler}
            />

            <PageWrapper className='flex flex-col flex-1 px-0'>
                <div className="flex justify-end mb-2">
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button disabled={!exportData} size="sm" className='flex gap-1'>
                                <Settings size={16}/> Export Settings
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[500px] p-4" side='left' align='start'>
                            {exportData && (
                                <ExportSettings
                                    exportData={exportData}
                                    columnVisibility={columnVisibility}
                                    setColumnVisibility={setColumnVisibility}
                                />
                            )}
                        </PopoverContent>
                    </Popover>
                </div>
                <div className="flex-1 w-full pe-2">
                    {(() => {
                        if (isLoadingExportData) {
                            return <LoaderWithBackdrop />
                        } else if (isErrorExportData) {
                            return (
                                <div className="grid w-full h-full place-items-center">
                                    <p className="text-lg font-medium">Error loading data</p>
                                </div>
                            )
                        } else {
                            return (
                                <DynamicHeightScrollArea offset={60}>
                                    <Table_NEW table={table} tableCellClassName="p-0" />
                                </DynamicHeightScrollArea>
                            )
                        }
                    })()}
                </div>

                <div className="flex justify-end mt-3 space-x-2">
                    <Link to={`..`}>
                        <Button variant="secondary" size='sm'>Back</Button>
                    </Link>
                    <Button disabled={isLoadingExportData} size='sm' onClick={beforeExportSurveyHandler}>Export</Button>
                </div>
            </PageWrapper>
        </>
    )
}